import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { Objective1, Status1, Result1, MilestoneMap1 } from '../../types';
import { useRef, useCallback } from 'react';
import { StatusCellPresenter } from './StatusCellPresenter';
import { useCurrentMap } from '../../hooks/useCurrentMap';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';
import { useParams, useSearchParams } from 'react-router-dom';
import { getStatusCellState } from '../../utils/statusUtils';
import { filterItemsForInterval } from '@mm-shared/intervals';
import { getInterval } from '@mm-shared/intervals';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { selectHighlightedAllocations } from '../../store/selectors/highlightSelectors';
import { isIntervalInHighlightRange } from '../../utils/highlightUtils';

/**
 * StatusCell3 — same visual appearance as StatusCell2, but click/enter/space
 * navigates to the Map3Drawer status tab instead of opening StatusTooltip1.
 *
 * Drop-in replacement for StatusCell2.  Same props interface (StatusCell2Props).
 * Does NOT render StatusTooltip1 — no Floating UI, no tooltip overlay.
 *
 * Navigation URL: /:userName/maps/:mapShortId/objectives/:objectiveShortId/status?forInterval=<forIntervalEnding>
 */

interface StatusCell3Props {
  objectiveUuid: string;
  objectiveIsParent: boolean;
  statusUuid: string;
  row: number;
  column: number;
  focusPosition: { row: number; col: number };
  hoverPosition: { row: number; col: number };
  forIntervalEnding: string;
  milestoneMapUuid: string;
  showResultCircles?: boolean;
  effectiveZoomLevel?: MilestoneMap1['interval'];
}

/**
 * Builds the status drawer search params for a given objective and interval.
 * Returns a new URLSearchParams with drawer, objectiveId, tab, and forInterval set,
 * preserving all other existing params.
 */
export function buildStatusDrawerParams(
  objectiveShortId: string,
  forIntervalEnding: string,
  existingSearch: string,
): URLSearchParams {
  const next = new URLSearchParams(existingSearch);
  next.set('drawer', 'objective');
  next.set('objectiveId', objectiveShortId);
  next.set('tab', 'status');
  next.set('forInterval', forIntervalEnding);
  return next;
}

function StatusCell3ReduxWrapper(props: StatusCell3Props) {
  const {
    objectiveUuid,
    objectiveIsParent,
    statusUuid,
    row,
    column,
    focusPosition,
    hoverPosition,
    forIntervalEnding,
    milestoneMapUuid,
    showResultCircles = true,
    effectiveZoomLevel,
  } = props;

  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj: Objective1) => obj.uuid === objectiveUuid),
  );
  const status = objective?.statuses.find((s: Status1) => s.uuid === statusUuid);

  const cellRef = useRef<HTMLDivElement>(null);

  const { mapShortId } = useParams<{
    mapShortId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive active objective from ?objectiveId= and ?tab= query params
  const activeObjectiveShortId = searchParams.get('objectiveId') ?? undefined;
  const activeTab = searchParams.get('tab') ?? undefined;

  // Check user permissions
  const currentMap = useCurrentMap();
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  // If this objective does not have a shortId yet, derive one from uuid
  const objectiveShortId =
    objective?.shortId || (objective?.uuid ? getShortIdFromUuid(objective.uuid) : undefined);

  const handleNavigateToStatus = useCallback(() => {
    if (objectiveIsParent) return;
    if (!mapShortId || !objectiveShortId) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('drawer', 'objective');
      next.set('objectiveId', objectiveShortId);
      next.set('tab', 'status');
      next.set('forInterval', forIntervalEnding);
      return next;
    });
  }, [objectiveIsParent, mapShortId, objectiveShortId, forIntervalEnding, setSearchParams]);

  function handleClick(e: React.MouseEvent) {
    if (objectiveIsParent) return;
    if (e.shiftKey) return; // Shift+Click used for multi-cell selection
    if (cellRef.current) {
      e.preventDefault();
      cellRef.current.focus({ preventScroll: true });
    }
    handleNavigateToStatus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (objectiveIsParent) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNavigateToStatus();
    }
  }

  function handleFocus(e: React.FocusEvent) {
    if (cellRef.current) {
      e.stopPropagation();
      cellRef.current.focus({ preventScroll: true });
    }
  }

  function handleBlur() {
    // no-op — no interactive-focus class needed (no tooltip to track)
  }

  // Read display settings from Redux
  const showStatusCodes = useSelector(
    (state: RootState) => state.map3UI.mapControls.showStatusCodes,
  );
  const toggleStatusCellDisplay = useSelector(
    (state: RootState) => state.map3UI.mapControls.toggleStatusCellDisplay,
  );
  const enableCrosshairs = useSelector(
    (state: RootState) => state.map3UI.mapControls.enableCrosshairs,
  );

  // Result circle highlight: read highlightedResultUuid to pass to ResultCirclesSVG
  const highlightedResultUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedResultUuid,
  );

  // Human highlight state (task mr-aqs.29.3)
  const highlightedAllocations = useSelector(selectHighlightedAllocations);
  const enableHumanHighlight = highlightedAllocations.length > 0;

  // Objective highlight (task mr-aqs.29.1)
  const highlightedObjectiveUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedObjectiveUuid,
  );
  const isObjectiveHighlighted =
    highlightedObjectiveUuid !== null && highlightedObjectiveUuid === objectiveUuid;

  // Drawer crosshairs: read highlightedIntervalEnding for Status tab column highlight (task mr-aqs.29.2)
  const highlightedIntervalEnding = useSelector(
    (state: RootState) => state.map3UI.highlightedIntervalEnding,
  );

  const enableResultCircleFill = toggleStatusCellDisplay === 'status_results2_comments';
  const derivedShowResultCircles = toggleStatusCellDisplay !== 'status';

  // Determine if this cell is the active one (drawer open for this objective+interval via status tab).
  // Check ?objectiveId=, ?tab=status, and ?forInterval= query params.
  const activeForInterval = searchParams.get('forInterval');
  const isActiveStatusCell =
    !objectiveIsParent &&
    !!activeObjectiveShortId &&
    !!objectiveShortId &&
    activeObjectiveShortId === objectiveShortId &&
    activeTab === 'status' &&
    activeForInterval === forIntervalEnding;

  const statusText = objectiveIsParent
    ? ''
    : objective?.type === 'metric' && status?.value != null
      ? String(status.value)
      : status?.statusCode || '';

  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.uuid === milestoneMapUuid),
  );
  const interval = milestoneMap
    ? getInterval(forIntervalEnding, milestoneMap, effectiveZoomLevel)
    : undefined;
  const { hasResults, hasKeyResults, hasComments } =
    objective && interval
      ? getStatusCellState(objective, interval)
      : { hasResults: false, hasKeyResults: false, hasComments: false };

  const resultsForInterval =
    objective && interval
      ? filterItemsForInterval<Result1>(objective.results, interval.start, interval.end)
      : [];

  // Compute whether this cell is highlighted by the human highlight feature (task mr-aqs.29.3)
  const isHighlightedCell = enableHumanHighlight && interval
    ? highlightedAllocations.some(
        (alloc) =>
          alloc.objectiveUuid === objectiveUuid &&
          isIntervalInHighlightRange(interval.start, interval.end, alloc.startDate, alloc.endDate),
      )
    : false;

  return (
    <StatusCellPresenter
      objectiveIsParent={objectiveIsParent}
      statusText={statusText}
      statusCode={status?.statusCode || ''}
      hasResults={hasResults}
      hasKeyResults={hasKeyResults}
      hasComments={hasComments}
      resultsForInterval={resultsForInterval}
      row={row}
      column={column}
      cellRef={cellRef}
      handleClick={handleClick}
      handleKeyDown={handleKeyDown}
      handleFocus={handleFocus}
      handleBlur={handleBlur}
      showStatusCodes={showStatusCodes}
      // No tooltip props — StatusCell3 does not render StatusTooltip1
      activateTooltip={false}
      statusUuid={statusUuid}
      objectiveUuid={objectiveUuid}
      forIntervalEnding={forIntervalEnding}
      milestoneMapUuid={milestoneMapUuid}
      focusPosition={focusPosition}
      hoverPosition={hoverPosition}
      enableCrosshairs={enableCrosshairs}
      enableResultCircleFill={enableResultCircleFill}
      showResultCircles={showResultCircles && derivedShowResultCircles}
      toggleStatusCellDisplay={toggleStatusCellDisplay}
      canEdit={canEdit}
      isActiveStatusCell={isActiveStatusCell}
      highlightedResultUuid={highlightedResultUuid}
      isObjectiveHighlighted={isObjectiveHighlighted}
      enableHumanHighlight={enableHumanHighlight}
      isHighlightedCell={isHighlightedCell}
      highlightedIntervalEnding={highlightedIntervalEnding}
      isHighlightedCrosshairRow={
        highlightedObjectiveUuid !== null && highlightedObjectiveUuid === objectiveUuid
      }
    />
  );
}

export function StatusCell3(props: StatusCell3Props) {
  return <StatusCell3ReduxWrapper {...props} />;
}
